import { get } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { connectNLP } from '../../../utilities/connect-navigate';
import { arrayToClass } from '../../../utilities/helper-fuctions';
import { RequestManager } from '../../../utilities/request';
import { ToastMessages } from '../../notifications/toast-messages';
import { Spinner } from '../../ui-components/spinner/spinner';
import { StatusMarker } from '../../ui-components/statusmarker/status-marker';

const mapStateProps = (store: any) => ({
    user: store.user.user,
});

const mapDispatchProps = () => ({});

class MyManagedProvidersComponent extends React.Component<any, any> {
    public state: any = {
        loading: false,
        providers: [],
        airports: {},
        editingAid: null,
        editingMessage: '',
        saving: false,
    };

    public componentDidMount() {
        this.fetchProviders();
    }

    public async fetchProviders(): Promise<void> {
        this.setState({ loading: true });

        try {
            const response = await RequestManager.get(
                '/providers/managed',
                { page: 0, size: 50, includeArchived: false }
            );

            if (!response) {
                throw new Error('Failed to load managed providers');
            }

            const providers = get(response, 'content') || [];

            this.setState({ providers, loading: false });
            this.fetchAirports(providers);
        } catch (err) {
            this.setState({ loading: false });
            toast.error(<ToastMessages error={err} />, { theme: 'light' });
        }
    }

    public async fetchAirports(providers: any[]): Promise<void> {
        const aids = Array.from(
            new Set(
                providers
                    .map((p) => get(p, 'parent'))
                    .filter((aid) => !!aid && !this.state.airports[aid])
            )
        );

        if (!aids.length) {
            return;
        }

        const results = await Promise.all(
            aids.map(async (aid) => {
                try {
                    const airport = await RequestManager.get(`/airports/${aid}`);
                    return [aid, airport];
                } catch {
                    return [aid, null];
                }
            })
        );

        const airports = { ...this.state.airports };
        results.forEach(([aid, airport]) => {
            airports[aid as string] = airport;
        });

        this.setState({ airports });
    }

    public startEditing(provider: any) {
        this.setState({
            editingAid: provider.aid,
            editingMessage: provider.featuredMessage || '',
        });
    }

    public cancelEditing() {
        this.setState({ editingAid: null, editingMessage: '' });
    }

    public async saveMessage(aid: string): Promise<void> {
        this.setState({ saving: true });

        try {
            await RequestManager.patch(
                `/providers/${aid}/featured-message`,
                { featuredMessage: this.state.editingMessage || null }
            );

            this.setState({ editingAid: null, editingMessage: '', saving: false });
            this.fetchProviders();
            toast.success('Featured message updated', { theme: 'light' });
        } catch (err) {
            this.setState({ saving: false });
            toast.error(<ToastMessages error={err} />, { theme: 'light' });
        }
    }

    public render(): React.ReactElement {
        return (
            <div className='w-100'>
                <div className='display-flex align-items-center mb-4'>
                    <h6 className='fw-bold text-uppercase palette--c-neutral-5 flex-fill m-0'>
                        Providers Managed by Me
                    </h6>
                </div>
                {this.state.loading ? <Spinner size='large' /> : this.renderProviders()}
            </div>
        );
    }

    public renderProviders() {
        const providers = this.state.providers;

        if (!providers.length) {
            return (
                <div className='w-100 display-flex justify-content-center palette--bc-neutral-4 border-1 border-radius-1'>
                    <p className='m-0 py-5 palette--c-neutral-5'>
                        No managed providers found.
                    </p>
                </div>
            );
        }

        return (
            <div className='w-100'>
                <div className='m-2'>
                    <div className='row px-2'>
                        <h5 className='palette--c-neutral-5 text-uppercase col-5'>Name</h5>
                        <h5 className='palette--c-neutral-5 text-uppercase col-4'>Category</h5>
                        <h5 className='palette--c-neutral-5 text-uppercase col-4'>Airport</h5>
                        <h5 className='palette--c-neutral-5 text-uppercase col-8'>Featured Message</h5>
                        <h5 className='palette--c-neutral-5 text-uppercase col-3'>Actions</h5>
                    </div>
                </div>
                <div className='m-2 palette--bc-neutral-4 border-1 border-radius-1'>
                    {providers.map((item, index) => this.renderRow(item, index))}
                </div>
            </div>
        );
    }

    public renderAirportCell(item: any) {
        const parent = get(item, 'parent');

        if (!parent) {
            return <span className='palette--c-neutral-5'>—</span>;
        }

        const airport = this.state.airports[parent];
        const label = get(airport, 'icao') || get(airport, 'iata') || parent;

        return (
            <span
                className='pointer palette--c-primary-4 fw-bold'
                onClick={() => this.props.router.navigate(`/airports/${parent}`)}
            >
                {label}
            </span>
        );
    }

    public renderProviderNameCell(item: any) {
        const name = get(item, 'name') || '—';
        const parent = get(item, 'parent');
        const aid = get(item, 'aid');

        if (!parent || !aid) {
            return <span className='palette--c-neutral-6 fw-bold'>{name}</span>;
        }

        return (
            <span
                className='pointer palette--c-primary-4 fw-bold'
                onClick={() =>
                    this.props.router.navigate(`/airports/${parent}/${aid}`)
                }
            >
                {name}
            </span>
        );
    }

    public renderRow(item: any, index: number) {
        const classes = arrayToClass([
            `palette--bgc-neutral-${index % 2 === 0 ? '1' : '3'}`,
            'w-100 p-2',
        ]);
        const isEditing = this.state.editingAid === item.aid;

        return (
            <div key={item.aid} className={classes}>
                <div className='row display-flex align-items-center'>
                    <div className='col-5'>
                        {this.renderProviderNameCell(item)}
                    </div>
                    <div className='col-4'>
                        <StatusMarker
                            tColor='secondary-4'
                            bgColor='secondary-1'
                            text={get(item, 'category') || '—'}
                            type='tag'
                        />
                    </div>
                    <div className='col-4'>
                        {this.renderAirportCell(item)}
                    </div>
                    <div className='col-8'>
                        {isEditing ? (
                            <input
                                className='w-100 border-1 palette--bc-neutral-4 border-radius-1 p-1'
                                value={this.state.editingMessage}
                                maxLength={280}
                                onChange={(e) => this.setState({ editingMessage: e.target.value })}
                                placeholder='Enter featured message (max 280 chars)'
                            />
                        ) : (
                            <span className='palette--c-neutral-5'>
                                {get(item, 'featuredMessage') || '—'}
                            </span>
                        )}
                    </div>
                    <div className='col-3'>
                        {isEditing ? (
                            <div className='display-flex'>
                                <span
                                    className='pointer palette--c-primary-4 fw-bold mr-2'
                                    onClick={() => this.saveMessage(item.aid)}
                                >
                                    {this.state.saving ? '...' : 'Save'}
                                </span>
                                <span
                                    className='pointer palette--c-neutral-5'
                                    onClick={() => this.cancelEditing()}
                                >
                                    Cancel
                                </span>
                            </div>
                        ) : (
                            <span
                                className='pointer palette--c-primary-4 fw-bold'
                                onClick={() => this.startEditing(item)}
                            >
                                Edit
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export const MyManagedProviders: any = connectNLP(
    connect(
        mapStateProps,
        mapDispatchProps,
        null,
        { forwardRef: true }
    )(MyManagedProvidersComponent)
);
